import type { DeliveriesDeliveryFiltersResponse } from '@customerio/hydra-ui-api';
import type { IconName } from '@customerio/pluma-icons';

/**
 * The picker's view of one message, derived from `deliveries/filters` — the
 * same endpoint and the same transformation the Ember filters service applies,
 * so pickers in both apps offer identical entries.
 */
export type PickerMessageParent = {
  id: string;
  type: 'campaign' | 'broadcast' | 'newsletter' | 'transactional message' | 'in_app_broadcast' | 'any';
  name: string;
  campaignType?: string;
  output_id?: string;
};

export type PickerMessage = {
  output_id: string;
  subject: string;
  type: string;
  /** Language-branch entries only: the action id the selectable `output_*` value derives from. */
  id?: string;
  campaign_id?: string | null;
  newsletter_id?: string | null;
  /** The variation's template id — what a newsletter-recipients condition narrows on. Branch entries carry none. */
  template_id?: string;
  variation?: number | null;
  language?: string | null;
  can_segment_on_variation?: boolean;
  is_segmentable_variation?: boolean;
  parent?: PickerMessageParent;
  searchLabel?: string;
  sort_value?: string;
  isSplit?: boolean;
  isLanguageBranch?: boolean;
  // Picker presentation flags carried per entry.
  hideIcon?: boolean;
  hideParent?: boolean;
  isDeleted?: boolean;
  isAny?: boolean;
  isAnyInParent?: boolean;
  isTopLevelWildcard?: boolean;
  isGroupWildcard?: boolean;
  showLeftOffset?: boolean;
};

export type SegmentablePickerData = {
  segmentable: PickerMessage[];
  /** Language-branch ("All Variations") output ids → their channel. */
  branchMessageTypes: Map<string, string>;
};

const CAMPAIGN_TYPE_ICONS = new Map<string, IconName>([
  ['behavioral', 'segment'],
  ['seg_attr', 'segment'],
  ['rel_seg_attr', 'object'],
  ['transactional', 'trigger-event'],
  ['triggered_broadcast', 'api-triggered-broadcast'],
  ['date', 'trigger-date'],
  ['form', 'form'],
  ['webhook', 'webhook'],
  ['object', 'object'],
  ['relationship', 'connections'],
  ['geofence', 'geofences'],
  ['none', 'campaigns'],
]);

export function campaignTypeIcon(campaignType: string | undefined): IconName {
  return (campaignType != null ? CAMPAIGN_TYPE_ICONS.get(campaignType) : undefined) ?? 'campaigns';
}

function compareCaseInsensitive(a: string | undefined, b: string | undefined): number {
  const left = (a ?? '').toLowerCase();
  const right = (b ?? '').toLowerCase();
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

function isFirstVariation(message: PickerMessage): boolean {
  return message.variation === 1;
}

function parentOutputIdForVariation(outputId: string): string {
  const variationIndex = outputId.lastIndexOf('_variation_');
  return variationIndex < 0 ? outputId : outputId.slice(0, variationIndex);
}

function isPrimary(message: PickerMessage): boolean {
  return message.variation == null || isFirstVariation(message);
}

// fallow-ignore-next-line complexity -- one pass building the four parent maps; splitting obscures the lookup order
function parentFinder(filters: DeliveriesDeliveryFiltersResponse) {
  const campaigns = new Map<string, PickerMessageParent>();
  for (const campaign of filters.campaigns) {
    campaigns.set(campaign.id, {
      id: campaign.id,
      type: campaign.type === 'triggered_broadcast' ? 'broadcast' : 'campaign',
      name: campaign.name,
      campaignType: campaign.type,
    });
  }
  const newsletters = new Map<string, PickerMessageParent>();
  for (const newsletter of filters.newsletters) {
    newsletters.set(newsletter.id, { id: newsletter.id, type: 'newsletter', name: newsletter.name });
  }
  const transactional = new Map<string, PickerMessageParent>();
  for (const message of filters.transactional_messages) {
    transactional.set(message.id, { id: message.id, type: 'transactional message', name: message.name });
  }
  const inAppBroadcasts = new Map<string, PickerMessageParent>();
  for (const broadcast of filters.in_app_broadcasts) {
    inAppBroadcasts.set(broadcast.id, {
      id: broadcast.id,
      type: 'in_app_broadcast',
      name: broadcast.name,
      output_id: broadcast.output_id,
    });
  }

  return (message: {
    campaign_id?: string | null;
    newsletter_id?: string | null;
    transactional_message_id?: string | null;
    in_app_broadcast_id?: string | null;
  }): PickerMessageParent | undefined =>
    (message.campaign_id != null ? campaigns.get(message.campaign_id) : undefined) ??
    (message.newsletter_id != null ? newsletters.get(message.newsletter_id) : undefined) ??
    (message.transactional_message_id != null ? transactional.get(message.transactional_message_id) : undefined) ??
    (message.in_app_broadcast_id != null ? inAppBroadcasts.get(message.in_app_broadcast_id) : undefined);
}

// Keyed on the response object so every caller of the same payload shares one
// result — which keeps `segmentable`'s identity stable, and with it the
// ordering cache in message-grouping.ts (a WeakMap keyed on that array).
// Mirrors the Ember filters service, which computes the list once as a
// singleton.
const pickerDataCache = new WeakMap<DeliveriesDeliveryFiltersResponse, SegmentablePickerData>();

/**
 * The segmentable message list the picker offers: every primary message plus,
 * for newsletters that track variation segmentation, an "All Variations"
 * entry followed by the individual variations. Language-branch entries carry
 * `isLanguageBranch` and register their channel in `branchMessageTypes` (the
 * only way to resolve an `output_*` value's channel). Cached per response
 * object; the result must not be mutated.
 */
export function segmentablePickerData(filters: DeliveriesDeliveryFiltersResponse): SegmentablePickerData {
  const cached = pickerDataCache.get(filters);
  if (cached != null) {
    return cached;
  }
  const data = computeSegmentablePickerData(filters);
  pickerDataCache.set(filters, data);
  return data;
}

// fallow-ignore-next-line complexity -- the legacy transform's variation/branch rules, kept in one auditable pass
function computeSegmentablePickerData(filters: DeliveriesDeliveryFiltersResponse): SegmentablePickerData {
  const findParent = parentFinder(filters);
  const branchMessageTypes = new Map<string, string>();

  const branchMessages: PickerMessage[] = filters.branch_messages.map((message) => {
    const parent = findParent(message);
    branchMessageTypes.set(message.output_id, message.type);
    return {
      output_id: message.output_id,
      id: message.id,
      type: message.type,
      campaign_id: message.campaign_id,
      isLanguageBranch: true,
      parent,
      searchLabel: `${message.subject} ${parent?.name ?? ''} All Variations`.trim(),
      sort_value: message.subject,
      subject: `${message.subject} - All Variations`,
    };
  });

  const messages: PickerMessage[] = filters.messages.map((message) => {
    const parent = findParent(message);
    // `can_segment_on_variation` means the message was sent after template_id
    // tracking began; segmenting on variations is only offered for newsletters.
    const newsletterParent = parent?.type === 'newsletter' ? parent : null;
    const isSegmentableVariation = newsletterParent != null && message.can_segment_on_variation;
    return {
      output_id: message.output_id,
      subject: message.subject,
      type: message.type,
      campaign_id: message.campaign_id,
      newsletter_id: message.newsletter_id,
      template_id: message.template_id,
      variation: message.variation,
      language: message.language,
      can_segment_on_variation: message.can_segment_on_variation,
      is_segmentable_variation: isSegmentableVariation,
      parent,
      searchLabel: `${message.subject} ${parent?.name ?? ''}`.trim(),
      sort_value: isSegmentableVariation ? newsletterParent.name : message.subject,
    };
  });

  const sorted = [...messages, ...branchMessages].sort((a, b) => compareCaseInsensitive(a.sort_value, b.sort_value));

  const segmentable: PickerMessage[] = [];
  const addedAllVariationsOutputIds = new Set<string>();
  for (const message of sorted) {
    const isSegmentableVariation = message.is_segmentable_variation === true;

    // The first of a set of segmentable newsletter variations also emits an
    // "All Variations" entry ahead of the per-variation ones.
    if (isSegmentableVariation && (isFirstVariation(message) || (message.language ?? '') !== '')) {
      const outputId = parentOutputIdForVariation(message.output_id);
      if (!addedAllVariationsOutputIds.has(outputId)) {
        const combined: PickerMessage = {
          ...message,
          output_id: outputId,
          is_segmentable_variation: false,
          subject: `${message.subject} - All Variations`,
          isSplit: true,
        };
        delete combined.language;
        segmentable.push(combined);
        addedAllVariationsOutputIds.add(outputId);
      }
    }

    if (isSegmentableVariation || isPrimary(message)) {
      const copy: PickerMessage = {
        ...message,
        isSplit: !isSegmentableVariation && message.variation != null,
      };
      if (copy.isSplit === true) {
        copy.subject = `${copy.subject} - All Variations`;
      }
      segmentable.push(copy);
    }
  }

  return { segmentable, branchMessageTypes };
}
